import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import confetti from 'canvas-confetti';
import { Trophy, Users, Store, Ticket, Play, CheckCircle, ArrowRight, Home } from 'lucide-react';

interface Participant {
  id: string;
  name: string;
  entries: number;
  storeName: string;
}

interface DrawPreviewData {
  runId: string;
  campaignName: string;
  instanceNumber: number;
  winnersToSelect: number;
  totalEntries: number;
  uniqueParticipants: number;
  participatingStores: number;
  participants: Participant[];
  stores: { id: string; name: string }[];
}

interface Winner {
  prizeRank: number;
  customerId: string;
  customerName: string;
  customerEmail: string;
  totalEntries: number;
}

interface DrawResult {
  winners: Winner[];
  seed: string;
  totalEntries: number;
  uniqueParticipants: number;
}

type DrawPhase = 'loading' | 'ready' | 'drawing' | 'revealing' | 'complete';

export default function DrawWinnersPage() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<DrawPhase>('loading');
  const [previewData, setPreviewData] = useState<DrawPreviewData | null>(null);
  const [drawResult, setDrawResult] = useState<DrawResult | null>(null);
  const [currentWinnerIndex, setCurrentWinnerIndex] = useState(0);
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const shuffleIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const shuffleCountRef = useRef(0);

  // Load preview data
  useEffect(() => {
    const loadPreview = async () => {
      try {
        const res = await apiClient.get<{ success: boolean; data: DrawPreviewData }>(
          `/admin/campaigns/runs/${runId}/draw-preview`
        );
        setPreviewData(res.data.data);
        setPhase('ready');
      } catch (err: any) {
        setError(err.message || 'Failed to load draw data');
        setPhase('ready');
      }
    };
    loadPreview();
  }, [runId]);

  // Trigger confetti
  const fireConfetti = useCallback(() => {
    const duration = 3000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.7 },
        colors: ['#f59e0b', '#eab308', '#fbbf24', '#fcd34d'],
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.7 },
        colors: ['#f59e0b', '#eab308', '#fbbf24', '#fcd34d'],
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();

    // Big burst in the middle
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#f59e0b', '#eab308', '#fbbf24', '#fcd34d', '#ffffff'],
    });
  }, []);

  // Name shuffling animation
  const startShuffling = useCallback((participants: Participant[], winnerId: string, winnerName: string) => {
    shuffleCountRef.current = 0;
    const maxShuffles = 30;
    const names = participants.map(p => p.name);

    shuffleIntervalRef.current = setInterval(() => {
      shuffleCountRef.current++;

      if (shuffleCountRef.current >= maxShuffles) {
        // Final reveal
        if (shuffleIntervalRef.current) {
          clearInterval(shuffleIntervalRef.current);
        }
        setDisplayName(winnerName);
        setPhase('revealing');
        fireConfetti();
      } else {
        // Slow down as we approach the end
        const progress = shuffleCountRef.current / maxShuffles;
        const delay = 50 + progress * 200;

        // Random name while shuffling
        const randomName = names[Math.floor(Math.random() * names.length)];
        setDisplayName(randomName);

        if (shuffleIntervalRef.current) {
          clearInterval(shuffleIntervalRef.current);
          shuffleIntervalRef.current = setInterval(() => {
            shuffleCountRef.current++;
            if (shuffleCountRef.current >= maxShuffles) {
              if (shuffleIntervalRef.current) {
                clearInterval(shuffleIntervalRef.current);
              }
              setDisplayName(winnerName);
              setPhase('revealing');
              fireConfetti();
            } else {
              const newRandomName = names[Math.floor(Math.random() * names.length)];
              setDisplayName(newRandomName);
            }
          }, delay);
        }
      }
    }, 50);
  }, [fireConfetti]);

  // Start the draw
  const handleStartDraw = async () => {
    if (!previewData) return;

    setPhase('drawing');
    setDisplayName('');

    try {
      // Call the actual draw endpoint
      const res = await apiClient.post<{ success: boolean; data: DrawResult }>(
        `/admin/campaigns/runs/${runId}/draw`
      );
      setDrawResult(res.data.data);

      // Start revealing first winner
      const firstWinner = res.data.data.winners[0];
      if (firstWinner) {
        startShuffling(previewData.participants, firstWinner.customerId, firstWinner.customerName);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to draw winners');
      setPhase('ready');
    }
  };

  // Move to next winner
  const handleNextWinner = () => {
    if (!drawResult || !previewData) return;

    const nextIndex = currentWinnerIndex + 1;
    if (nextIndex >= drawResult.winners.length) {
      setPhase('complete');
      return;
    }

    setCurrentWinnerIndex(nextIndex);
    setPhase('drawing');

    const nextWinner = drawResult.winners[nextIndex];
    startShuffling(previewData.participants, nextWinner.customerId, nextWinner.customerName);
  };

  // Navigate back to winners dashboard
  const handleFinish = () => {
    navigate('/admin/winners');
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (shuffleIntervalRef.current) {
        clearInterval(shuffleIntervalRef.current);
      }
    };
  }, []);

  if (phase === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading draw data...</p>
        </div>
      </div>
    );
  }

  if (error && !previewData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Trophy className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Draw</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/admin/winners')}
            className="btn-primary"
          >
            Back to Winners
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex flex-col">
      {/* Header */}
      <div className="p-6 text-center">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
          {previewData?.campaignName}
        </h1>
        <p className="text-purple-300">
          Occurrence #{previewData?.instanceNumber} - Winner Drawing
        </p>
      </div>

      {/* Stats Cards */}
      {phase === 'ready' && previewData && (
        <div className="px-6 mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <StatCard
              icon={Ticket}
              label="Total Entries"
              value={previewData.totalEntries}
              color="amber"
            />
            <StatCard
              icon={Users}
              label="Participants"
              value={previewData.uniqueParticipants}
              color="blue"
            />
            <StatCard
              icon={Store}
              label="Stores"
              value={previewData.participatingStores}
              color="green"
            />
            <StatCard
              icon={Trophy}
              label="Winners to Draw"
              value={previewData.winnersToSelect}
              color="purple"
            />
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        {phase === 'ready' && (
          <div className="text-center">
            <div className="mb-8">
              <div className="w-32 h-32 mx-auto bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center mb-6 shadow-2xl shadow-amber-500/30">
                <Trophy className="w-16 h-16 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Ready to Draw Winners</h2>
              <p className="text-purple-300 max-w-md mx-auto">
                Click the button below to begin the winner selection.
                Winners will be drawn one at a time using weighted random selection.
              </p>
            </div>
            <button
              onClick={handleStartDraw}
              className="px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold text-xl rounded-xl shadow-lg shadow-amber-500/30 flex items-center gap-3 mx-auto transition-all hover:scale-105"
            >
              <Play className="w-6 h-6" fill="currentColor" />
              Start Drawing Winners
            </button>
          </div>
        )}

        {(phase === 'drawing' || phase === 'revealing') && (
          <div className="text-center w-full max-w-2xl">
            {/* Winner Number */}
            <div className="mb-6">
              <span className="inline-block px-4 py-2 bg-amber-500/20 text-amber-400 rounded-full text-sm font-medium">
                {currentWinnerIndex === 0 ? '1st' :
                 currentWinnerIndex === 1 ? '2nd' :
                 currentWinnerIndex === 2 ? '3rd' :
                 `${currentWinnerIndex + 1}th`} Place Winner
              </span>
            </div>

            {/* Name Display */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8 min-h-[160px] flex items-center justify-center">
              <h2 className={`text-4xl md:text-6xl font-bold transition-all duration-200 ${
                phase === 'revealing' ? 'text-amber-400 scale-110' : 'text-white'
              }`}>
                {displayName || '...'}
              </h2>
            </div>

            {/* Winner Details (shown after reveal) */}
            {phase === 'revealing' && drawResult && (
              <div className="mb-8 animate-fadeIn">
                <p className="text-purple-300 text-lg">
                  with <span className="text-white font-semibold">{drawResult.winners[currentWinnerIndex].totalEntries} entries</span>
                </p>
              </div>
            )}

            {/* Action Buttons */}
            {phase === 'revealing' && drawResult && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {currentWinnerIndex < drawResult.winners.length - 1 ? (
                  <button
                    onClick={handleNextWinner}
                    className="px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold rounded-xl shadow-lg flex items-center gap-2 justify-center transition-all hover:scale-105"
                  >
                    Draw Next Winner
                    <ArrowRight className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    onClick={() => setPhase('complete')}
                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-xl shadow-lg flex items-center gap-2 justify-center transition-all hover:scale-105"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Complete Drawing
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {phase === 'complete' && drawResult && (
          <div className="text-center w-full max-w-2xl">
            <div className="mb-8">
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mb-6 shadow-2xl shadow-green-500/30">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">Drawing Complete!</h2>
              <p className="text-purple-300">
                {drawResult.winners.length} winner{drawResult.winners.length !== 1 ? 's' : ''} selected
              </p>
            </div>

            {/* Winners Summary */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-white mb-4">Winners</h3>
              <div className="space-y-3">
                {drawResult.winners.map((winner, index) => (
                  <div
                    key={winner.customerId}
                    className="flex items-center gap-4 bg-white/5 rounded-xl p-4"
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                      index === 0 ? 'bg-amber-500' :
                      index === 1 ? 'bg-gray-400' :
                      index === 2 ? 'bg-amber-700' :
                      'bg-gray-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1 text-left">
                      <p className="text-white font-semibold">{winner.customerName}</p>
                      <p className="text-purple-300 text-sm">{winner.totalEntries} entries</p>
                    </div>
                    <Trophy className={`w-6 h-6 ${
                      index === 0 ? 'text-amber-400' :
                      index === 1 ? 'text-gray-300' :
                      index === 2 ? 'text-amber-600' :
                      'text-gray-400'
                    }`} />
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={handleFinish}
              className="px-6 py-3 bg-white text-gray-900 font-semibold rounded-xl shadow-lg flex items-center gap-2 justify-center mx-auto transition-all hover:scale-105"
            >
              <Home className="w-5 h-5" />
              Back to Winners Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  color: 'amber' | 'blue' | 'green' | 'purple';
}) {
  const colors = {
    amber: 'from-amber-500/20 to-amber-600/20 border-amber-500/30 text-amber-400',
    blue: 'from-blue-500/20 to-blue-600/20 border-blue-500/30 text-blue-400',
    green: 'from-green-500/20 to-green-600/20 border-green-500/30 text-green-400',
    purple: 'from-purple-500/20 to-purple-600/20 border-purple-500/30 text-purple-400',
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-4 text-center`}>
      <Icon className="w-6 h-6 mx-auto mb-2" />
      <p className="text-3xl font-bold text-white">{value.toLocaleString()}</p>
      <p className="text-sm opacity-80">{label}</p>
    </div>
  );
}
